// src/components/referral/ReferAndEarn.jsx
import React, { useState, useEffect } from 'react';
import referralService from '../../services/referralService';
import './ReferAndEarn.css';

const ReferAndEarn = () => {
  const [data, setData]           = useState(null);
  const [wallet, setWallet]       = useState(null);
  const [loading, setLoading]     = useState(true);
  const [error, setError]         = useState(null);
  const [copied, setCopied]       = useState(false);
  const [activeTab, setActiveTab] = useState('overview');

  const [applyCode, setApplyCode]       = useState('');
  const [applyLoading, setApplyLoading] = useState(false);
  const [applyMsg, setApplyMsg]         = useState(null);      // { type: 'success'|'error', text }
  const [codeApplied, setCodeApplied]     = useState(false);  // hide box once applied

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);
      const [refData, walletData] = await Promise.all([
        referralService.getMyReferral(),
        referralService.getWallet(),
      ]);
      setData(refData);
      setWallet(walletData);
      // If user already has a referrer, hide the apply-code box
      if (refData?.referred_by_id || refData?.already_referred) {
        setCodeApplied(true);
      }
    } catch (err) {
      console.error('Referral load error:', err);
      setError('Failed to load referral data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleApplyCode = async () => {
    const code = applyCode.trim().toUpperCase();
    if (!code) {
      setApplyMsg({ type: 'error', text: 'Please enter a referral code.' });
      return;
    }
    if (code.length < 4) {
      setApplyMsg({ type: 'error', text: 'Referral code is too short.' });
      return;
    }

    setApplyLoading(true);
    setApplyMsg(null);

    try {
      const res = await referralService.applyCode(code);
      if (res.success) {
        setApplyMsg({ type: 'success', text: res.message });
        // Reload everything so already_referred becomes true → hides the box
        await loadData();
      } else {
        setApplyMsg({
          type: res.already_used ? 'warn' : 'error',
          text: res.message || 'Failed to apply code.'
        });
      }
    } catch (err) {
      const data    = err?.response?.data;
      const msgText = data?.message || 'Something went wrong. Please try again.';
      setApplyMsg({
        type: data?.already_used ? 'warn' : 'error',
        text: msgText
      });
    } finally {
      setApplyLoading(false);
    }
  };

  const handleCopy = async () => {
    const code = data?.referral_code;
    if (!code) { alert('Referral code not ready. Please refresh.'); return; }
    try {
      await navigator.clipboard.writeText(code);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      const el = document.createElement('textarea');
      el.value = code;
      el.style.cssText = 'position:fixed;opacity:0;top:0;left:0';
      document.body.appendChild(el);
      el.focus(); el.select();
      try { document.execCommand('copy'); setCopied(true); setTimeout(() => setCopied(false), 2000); }
      catch { alert(`Your code: ${code} — copy it manually.`); }
      document.body.removeChild(el);
    }
  };

  const handleShare = async () => {
    const code = data?.referral_code;
    if (!code) { alert('Referral code not ready. Please refresh.'); return; }
    const shareUrl  = `${window.location.origin}/login?ref=${code}`;
    const shareText = `🚗 Join CarYugo! Use my referral code ${code} & get ₹50 off your first transaction.`;
    if (navigator.share) {
      try {
        await navigator.share({ title: 'CarYugo — Refer & Earn', text: shareText, url: shareUrl });
      } catch (err) {
        if (err.name !== 'AbortError') copyToClipboard(`${shareText}\n${shareUrl}`);
      }
    } else {
      copyToClipboard(`${shareText}\n${shareUrl}`);
    }
  };

  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      alert('✅ Share link copied to clipboard!');
    } catch {
      const el = document.createElement('textarea');
      el.value = text; el.style.cssText = 'position:fixed;opacity:0;top:0;left:0';
      document.body.appendChild(el); el.select(); document.execCommand('copy');
      document.body.removeChild(el); alert('✅ Share link copied to clipboard!');
    }
  };

  if (loading) {
    return (
      <div className="rae-loading">
        <div className="rae-spinner" />
        <p>Loading your referral dashboard…</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="rae-loading">
        <span style={{ fontSize: 48 }}>⚠️</span>
        <p style={{ color: '#c30000', margin: '8px 0' }}>{error}</p>
        <button className="rae-share-btn" style={{ maxWidth: 200 }} onClick={loadData}>🔄 Retry</button>
      </div>
    );
  }

  const referralCode = data?.referral_code;
  const hasCode      = !!referralCode;

  return (
    <div className="rae-container">

      {/* ── Hero ── */}
      <div className="rae-hero">
        <div className="rae-hero-text">
          <h2>Refer &amp; Earn 💰</h2>
          <p>Share your code. Earn ₹299 when your friend buys or sells a car.</p>
        </div>
        <div className="rae-hero-amounts">
          <div className="rae-amount-pill">
            <span className="rae-amt">₹299</span>
            <span className="rae-lbl">You Earn</span>
          </div>
          <div className="rae-amount-pill rae-secondary">
            <span className="rae-amt">₹50</span>
            <span className="rae-lbl">Friend Gets</span>
          </div>
        </div>
      </div>

      {/* ── Stats ── */}
      <div className="rae-stats-row">
        {[
          { num: data?.total_referrals ?? 0,                       lbl: 'Total Referred', cls: '' },
          { num: data?.rewarded_count  ?? 0,                       lbl: 'Completed',      cls: 'rae-stat-green' },
          { num: data?.pending_count   ?? 0,                       lbl: 'Pending',        cls: 'rae-stat-yellow' },
          { num: `₹${Number(data?.total_earned ?? 0).toFixed(0)}`, lbl: 'Total Earned',   cls: 'rae-stat-blue' },
        ].map((s, i) => (
          <div key={i} className={`rae-stat-card ${s.cls}`}>
            <span className="rae-stat-num">{s.num}</span>
            <span className="rae-stat-lbl">{s.lbl}</span>
          </div>
        ))}
      </div>

      {/* ── Your Referral Code Box ── */}
      <div className="rae-code-box">
        <p className="rae-code-label">Your Referral Code</p>
        {hasCode ? (
          <>
            <div className="rae-code-row">
              <span className="rae-code">{referralCode}</span>
              <button className="rae-copy-btn" onClick={handleCopy}>
                {copied ? '✅ Copied!' : '📋 Copy'}
              </button>
            </div>
            <p className="rae-url-preview">
              🔗 {window.location.origin}/login?ref={referralCode}
            </p>
          </>
        ) : (
          <div className="rae-code-row">
            <span className="rae-code-missing">Generating code…</span>
            <button className="rae-copy-btn" onClick={loadData}>🔄 Refresh</button>
          </div>
        )}
        <button
          className={`rae-share-btn${!hasCode ? ' rae-share-btn--disabled' : ''}`}
          onClick={handleShare}
          disabled={!hasCode}
          style={{ marginTop: 14 }}
        >
          🔗 Share Your Code
        </button>
      </div>

      {/* ── Have a Friend's Code? Apply Here ── */}
      {data?.already_referred ? (
        /* ── Already used a code — show info banner ── */
        <div className="rae-apply-box rae-apply-box--already">
          <span className="rae-apply-already-icon">🎟️</span>
          <div className="rae-apply-already-text">
            <strong>Referral Code Already Applied</strong>
            <p>
              You've already used{' '}
              {data.referrer_code_used
                ? <span className="rae-used-code">{data.referrer_code_used}</span>
                : 'a referral code'
              }
              {' '}on this account. Each account can only use one referral code.
            </p>
          </div>
        </div>
      ) : (
        /* ── Input box to apply a code ── */
        <div className="rae-apply-box">
          <p className="rae-code-label">Have a Friend's Referral Code?</p>
          <p className="rae-apply-subtext">
            Paste it below to get your <strong>₹50 welcome coupon</strong> instantly!
          </p>
          <div className="rae-apply-row">
            <input
              className="rae-apply-input"
              type="text"
              placeholder="e.g. ABCD1234"
              value={applyCode}
              onChange={e => {
                setApplyCode(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, ''));
                setApplyMsg(null);
              }}
              maxLength={12}
              disabled={applyLoading}
              onKeyDown={e => e.key === 'Enter' && handleApplyCode()}
            />
            <button
              className="rae-apply-btn"
              onClick={handleApplyCode}
              disabled={applyLoading || applyCode.trim().length < 4}
            >
              {applyLoading ? '⏳ Applying…' : '🎁 Apply Code'}
            </button>
          </div>

          {/* ── Feedback message ── */}
          {applyMsg && (
            <div className={`rae-apply-msg rae-apply-msg--${applyMsg.type}`}>
              {applyMsg.type === 'success' && '✅ '}
              {applyMsg.type === 'error'   && '❌ '}
              {applyMsg.type === 'warn'    && '⚠️ '}
              {applyMsg.text}
            </div>
          )}
        </div>
      )}

      {/* ── Wallet Strip ── */}
      <div className="rae-wallet-strip">
        <div className="rae-wallet-item">
          <span className="rae-wallet-icon">💳</span>
          <div>
            <span className="rae-wallet-amount">₹{Number(wallet?.wallet_balance ?? 0).toFixed(2)}</span>
            <span className="rae-wallet-lbl">Wallet Balance</span>
          </div>
        </div>
        <div className="rae-wallet-divider" />
        <div className="rae-wallet-item">
          <span className="rae-wallet-icon">🪙</span>
          <div>
            <span className="rae-wallet-amount">{wallet?.coins ?? 0}</span>
            <span className="rae-wallet-lbl">Coins</span>
          </div>
        </div>
        <div className="rae-wallet-divider" />
        <div className="rae-wallet-item">
          <span className="rae-wallet-icon">🎟️</span>
          <div>
            <span className="rae-wallet-amount">
              {wallet?.coupons?.filter(c => c.usable).length ?? 0}
            </span>
            <span className="rae-wallet-lbl">Active Coupons</span>
          </div>
        </div>
      </div>

      {/* ── Tabs ── */}
      <div className="rae-tabs">
        {['overview', 'referrals', 'transactions', 'coupons'].map(tab => (
          <button
            key={tab}
            className={`rae-tab ${activeTab === tab ? 'active' : ''}`}
            onClick={() => setActiveTab(tab)}
          >
            {tab.charAt(0).toUpperCase() + tab.slice(1)}
          </button>
        ))}
      </div>

      {/* ── Overview Tab ── */}
      {activeTab === 'overview' && (
        <div className="rae-how-it-works">
          <h3 className="rae-section-title">How It Works</h3>
          <div className="rae-steps">
            {[
              { icon: '🔗', title: 'Share Your Code',  desc: 'Share your unique referral code with friends & family.' },
              { icon: '📝', title: 'Friend Signs Up',  desc: 'They get ₹50 coupon instantly. You earn 30 coins.' },
              { icon: '🚗', title: 'Friend Transacts', desc: 'When they buy or sell a car — you earn ₹299 cash!' },
            ].map((s, i) => (
              <div key={i} className="rae-step">
                <div className="rae-step-icon">{s.icon}</div>
                <div className="rae-step-body"><h4>{s.title}</h4><p>{s.desc}</p></div>
              </div>
            ))}
          </div>
          <h3 className="rae-section-title" style={{ marginTop: 28 }}>Reward Summary</h3>
          <div className="rae-reward-table">
            <table>
              <thead><tr><th>Action</th><th>You Earn</th><th>Friend Gets</th></tr></thead>
              <tbody>
                <tr><td>Friend Signs Up</td><td>30 Coins</td><td>₹50 Coupon</td></tr>
                <tr><td>Friend Sells Car</td><td>₹299 Cash</td><td>₹100 Coupon</td></tr>
                <tr><td>Friend Buys Car</td><td>₹299 Cash</td><td>₹100 Discount</td></tr>
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ── Referrals Tab ── */}
      {activeTab === 'referrals' && (
        <div className="rae-list">
          {!data?.referrals?.length ? (
            <div className="rae-empty"><span>👥</span><p>No referrals yet. Share your code to start earning!</p></div>
          ) : data.referrals.map(r => (
            <div key={r.id} className="rae-referral-row">
              <div className="rae-ref-avatar">{(r.referred_user || 'U').charAt(0).toUpperCase()}</div>
              <div className="rae-ref-info">
                <strong>{r.referred_user}</strong>
                <span>{r.trigger_type ? `Completed: ${r.trigger_type}` : 'Signed up'}</span>
              </div>
              <div className="rae-ref-right">
                <span className={`rae-badge rae-badge-${r.status}`}>{r.status}</span>
                {r.reward_amount > 0 && <span className="rae-earned">+₹{r.reward_amount}</span>}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ── Transactions Tab ── */}
      {activeTab === 'transactions' && (
        <div className="rae-list">
          {!wallet?.transactions?.length ? (
            <div className="rae-empty"><span>💳</span><p>No transactions yet.</p></div>
          ) : wallet.transactions.map(t => (
            <div key={t.id} className="rae-txn-row">
              <span className={`rae-txn-icon ${t.type}`}>{t.type === 'credit' ? '↑' : '↓'}</span>
              <div className="rae-txn-info">
                <strong>{t.description}</strong>
                <span>{new Date(t.created_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}</span>
              </div>
              <span className={`rae-txn-amount ${t.type}`}>
                {t.type === 'credit' ? '+' : '-'}₹{Number(t.amount).toFixed(0)}
              </span>
            </div>
          ))}
        </div>
      )}

      {/* ── Coupons Tab ── */}
      {activeTab === 'coupons' && (
        <div className="rae-coupons-grid">
          {!wallet?.coupons?.length ? (
            <div className="rae-empty"><span>🎟️</span><p>No coupons yet. Start referring!</p></div>
          ) : wallet.coupons.map(c => (
            <div key={c.id} className={`rae-coupon-card ${!c.usable ? 'used' : ''}`}>
              <div className="rae-coupon-left">
                <span className="rae-coupon-icon">🎟️</span>
                <div>
                  <strong className="rae-coupon-amount">₹{c.discount_amount} OFF</strong>
                  <span className="rae-coupon-type">{c.coupon_type?.replace(/_/g, ' ')}</span>
                </div>
              </div>
              <div className="rae-coupon-right">
                <span className="rae-coupon-code">{c.code}</span>
                <span className={`rae-coupon-status ${c.usable ? 'active' : 'used'}`}>
                  {c.used ? 'Used' : c.usable ? 'Active' : 'Expired'}
                </span>
                {c.expires_at && (
                  <span className="rae-coupon-expiry">
                    Expires {new Date(c.expires_at).toLocaleDateString('en-IN')}
                  </span>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ReferAndEarn;